import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { http } from "@/lib/api";

export default function WorkDetail() {
    const { slug } = useParams();
    const [project, setProject] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        http.get(`/projects/${slug}`)
            .then((r) => setProject(r.data))
            .catch(() => setNotFound(true));
    }, [slug]);

    useEffect(() => {
        if (project) document.title = `${project.title} — MIDI Pictures`;
    }, [project]);

    if (notFound) {
        return (
            <main data-testid="work-detail-404" className="min-h-screen flex items-center justify-center px-6">
                <div className="text-center">
                    <p className="text-white/50 text-sm tracking-[0.3em] uppercase mb-4">404</p>
                    <h1 className="font-display tracking-tightest text-5xl md:text-7xl mb-8">Proyecto no encontrado</h1>
                    <Link to="/work" className="midi-link text-white">
                        Volver al archivo
                    </Link>
                </div>
            </main>
        );
    }

    if (!project) return <main className="min-h-screen bg-black" />;

    return (
        <main data-testid="work-detail-page" className="bg-black">
            <section className="relative h-[88svh] w-full overflow-hidden">
                <motion.img
                    initial={{ scale: 1.06 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
                    src={project.cover}
                    alt={project.title}
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black" />
                <div className="absolute inset-0 z-10 px-6 md:px-12 flex flex-col justify-end pb-12 md:pb-20">
                    <p className="text-[11px] tracking-[0.32em] uppercase text-white/70 mb-4">
                        {project.category} — {project.year}
                    </p>
                    <h1 className="font-display tracking-tightest leading-[0.78] text-5xl md:text-8xl lg:text-[140px] max-w-6xl">
                        {project.title}
                    </h1>
                </div>
            </section>

            <section className="px-6 md:px-12 py-20 md:py-32 grid grid-cols-12 gap-6 md:gap-10">
                <div className="col-span-12 md:col-span-4 space-y-8">
                    {project.client && (
                        <Meta label="Client" value={project.client} />
                    )}
                    <Meta label="Year" value={project.year} />
                    <Meta label="Category" value={project.category} />
                    {project.credits && <Meta label="Credits" value={project.credits} />}
                </div>
                <div className="col-span-12 md:col-span-7 md:col-start-6">
                    <p className="text-2xl md:text-4xl font-light leading-[1.2] tracking-tight text-white/90">
                        {project.description}
                    </p>
                </div>
            </section>

            {project.video_url && (
                <section className="px-6 md:px-12 pb-20">
                    <div className="relative w-full aspect-video bg-black">
                        <iframe
                            src={project.video_url}
                            title={project.title}
                            className="absolute inset-0 w-full h-full"
                            frameBorder="0"
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowFullScreen
                        />
                    </div>
                </section>
            )}

            {(project.images || []).length > 0 && (
                <section className="px-6 md:px-12 pb-32 space-y-6 md:space-y-10">
                    {project.images.map((src, i) => (
                        <motion.img
                            key={src}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                            src={src}
                            alt={`${project.title} still ${i + 1}`}
                            className="w-full object-cover aspect-[16/9]"
                        />
                    ))}
                </section>
            )}

            <section className="px-6 md:px-12 pb-32 border-t border-white/5 pt-16 flex items-center justify-between">
                <Link to="/work" className="midi-link text-white text-sm tracking-[0.32em] uppercase">
                    ← Archive
                </Link>
                <Link to="/contact" className="midi-link text-white text-sm tracking-[0.32em] uppercase">
                    Iniciar proyecto →
                </Link>
            </section>
        </main>
    );
}

function Meta({ label, value }) {
    return (
        <div>
            <p className="text-[10px] tracking-[0.32em] uppercase text-white/40 mb-2">{label}</p>
            <p className="text-white/90 text-base">{value}</p>
        </div>
    );
}
